import React from 'react';
import { motion } from 'framer-motion';
import { Clock, MapPin, Phone, Mail, Instagram, Facebook, Sparkles, Navigation } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ContactSection = () => {
  const { t, language } = useLanguage();

  return (
    <section 
      id="contact" 
      className="relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Premium Section Header */}
      <div className="relative h-48 md:h-64 overflow-hidden bg-gradient-to-b from-hanse-navy to-hanse-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-hanse-red rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-amber-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          {/* Sparkle Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-2 mb-4 bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm px-4 py-2 rounded-full border border-blue-400/30"
          >
            <Sparkles className="w-5 h-5 text-blue-400" />
            <span className="text-blue-300 font-semibold text-sm uppercase tracking-wider">
              {language === 'de' ? 'Besuchen Sie Uns' : 'Visit Us'}
            </span>
            <Sparkles className="w-5 h-5 text-blue-400" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white"
          >
            {t('contact.title')}
          </motion.h2>
        </div>
      </div>

      {/* Premium Content */}
      <div className="bg-gradient-to-b from-hanse-dark to-hanse-navy py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8">
            
            {/* Left: Contact Info Cards */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              {/* Opening Hours Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 md:p-6 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 md:w-6 md:h-6 text-amber-400" />
                  </div>
                  <h3 className="font-heading text-lg md:text-xl font-bold text-white">
                    {t('contact.hours')}
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-lg p-3 md:p-4">
                    <p className="text-white/50 text-xs md:text-sm mb-1">
                      {language === 'de' ? 'Montag - Sonntag' : 'Monday - Sunday'}
                    </p>
                    <p className="text-amber-400 font-bold text-base md:text-lg">11:00 - 22:00</p>
                  </div>
                </div>
              </div>

              {/* Contact Details Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 md:p-6 border border-white/10">
                <div className="space-y-4">
                  {/* Address */}
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 md:w-6 md:h-6 text-amber-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white text-sm md:text-base">{t('contact.address')}</h4>
                      <p className="text-white/60 text-sm">Industriestraße 12-12A</p>
                      <p className="text-white/60 text-sm">28199 Bremen Neustadt</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 md:w-6 md:h-6 text-amber-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white text-sm md:text-base">{t('contact.phone')}</h4>
                      <a href="tel:+4942148535145" className="text-amber-400 hover:text-amber-300 transition-colors font-bold text-sm md:text-base">
                        +49 421 48535145
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 md:w-6 md:h-6 text-amber-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white text-sm md:text-base">{t('contact.email')}</h4>
                      <a href="mailto:Info@hansepizzabremen.de" className="text-white/60 hover:text-amber-400 transition-colors text-sm md:text-base">
                        Info@hansepizzabremen.de
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 md:p-6 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                    <Instagram className="w-5 h-5 md:w-6 md:h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg md:text-xl font-bold text-white">
                      {t('contact.followUs')}
                    </h3>
                    <p className="text-white/50 text-xs md:text-sm">
                      {t('contact.followUsSubtitle')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://www.instagram.com/hansepizza?igsh=ZnZvejlqdnczdjBk&utm_source=qr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white/5 px-4 py-2 md:px-5 md:py-3 rounded-full text-white/80 hover:text-amber-400 transition-all border border-white/20 hover:border-amber-400/50"
                    data-testid="social-instagram"
                  >
                    <Instagram className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="text-sm font-medium">Instagram</span>
                  </a>
                  <a
                    href="https://www.facebook.com/share/17iyAZZSBE/?mibextid=wwXIfr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white/5 px-4 py-2 md:px-5 md:py-3 rounded-full text-white/80 hover:text-amber-400 transition-all border border-white/20 hover:border-amber-400/50"
                    data-testid="social-facebook"
                  >
                    <Facebook className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="text-sm font-medium">Facebook</span>
                  </a>
                  <a
                    href="https://tiktok.com/@hansepizzabremen"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white/5 px-4 py-2 md:px-5 md:py-3 rounded-full text-white/80 hover:text-amber-400 transition-all border border-white/20 hover:border-amber-400/50"
                    data-testid="social-tiktok"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 md:w-5 md:h-5">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                    <span className="text-sm font-medium">TikTok</span>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Right: Google Map */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="h-full"
            >
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-2 border border-white/10 h-full min-h-[400px] md:min-h-[500px]">
                <div className="rounded-xl overflow-hidden h-full relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2393.1234567890123!2d8.7891234!3d53.0678901!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sIndustriestraße%2012%2C%2028199%20Bremen!5e0!3m2!1sen!2sde!4v1234567890123"
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: '380px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Hanse Pizza Location"
                    className="w-full h-full"
                    data-testid="google-map"
                  />
                  
                  {/* Map Overlay Badge */}
                  <motion.a
                    href="https://www.google.com/maps/dir//Industriestraße+12,+28199+Bremen"
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-auto bg-hanse-dark/90 backdrop-blur-sm px-4 py-3 rounded-xl border border-white/20 flex items-center gap-3 hover:border-amber-400/50 transition-all group"
                  >
                    <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Navigation className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm group-hover:text-amber-400 transition-colors">
                        {language === 'de' ? 'Route berechnen' : 'Get Directions'}
                      </p>
                      <p className="text-white/50 text-xs">Google Maps</p>
                    </div>
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
